import React, { useEffect, useMemo, useState } from "react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from "recharts";

const PUB = process.env.PUBLIC_URL || "";

/* ═══ JANUS · BRIGHT THEME ═══════════════════════════════════════════
   Bright dashboard, bright text — deliberately lighter than MIDAS
   (which is deep-black/gold). Janus lives in a marble-cream sanctum. */
const CREAM      = "#faf6ee";
const CREAM_SOFT = "#f2ecdf";
const ROSE_TOP   = "#f7ede4";   // rose-gold cream, header end of scroll
const ROSE_BOT   = "#efdfd0";   // deeper rose-gold cream, footer end of scroll
const INK        = "#1c1a15";     // near-black for legibility
const INK_SOFT   = "#4a4638";
const GOLD       = "#b8892b";
const GOLD_SOFT  = "#e6c98a";
const ROSE_GOLD  = "#c99987";   // Guild Academy brand hue
const EMERALD    = "#0e8a5b";
const RUBY       = "#c22a3d";
const LINE       = "rgba(28,26,21,0.10)";
const CARD_BG    = "#ffffff";

// V1 UNIFIED · locked 8 Sep 2026 · one bot, no tiers
// Backtest: yfinance 1H · 6 majors · dual engine · 0.4% risk · MTD guardrail + per-pair regime kill
const BT = {
    strategy: "Dual-engine fade — fake-break + range extreme",
    pairs: ["EUR/USD", "GBP/USD", "USD/JPY", "AUD/USD", "USD/CAD", "EUR/JPY"],
    pair_stats: [
        { p: "EUR/USD", n: 253, wr: 39.1, pf: 1.58 },
        { p: "GBP/USD", n: 255, wr: 36.1, pf: 1.49 },
        { p: "USD/JPY", n: 200, wr: 33.5, pf: 1.61 },
        { p: "AUD/USD", n: 210, wr: 38.1, pf: 1.51 },
        { p: "USD/CAD", n: 291, wr: 45.4, pf: 1.52 },
        { p: "EUR/JPY", n: 227, wr: 26.4, pf: 1.52 },
    ],
    risk_pct: 0.4,
    trades: 1436,
    trades_per_year: 515,
    regime_skipped: 314,
    win_rate: 36.9,
    profit_factor: 1.53,
    total_return: 185.72,
    cagr: 45.70,
    max_dd: -8.62,
    sharpe: 3.36,
    initial: 10000,
    final: 28571.69,
    exits: { tp: 460, sl: 434, timeout: 542 }, // approx from raw signal distribution
    rebate_per_10k: 3604,
    directional_per_10k: 4570,
    total_yield_pct: 81.7,
};
const BT_DAYS = 1018; // ~2.79 years

// Synthetic monthly equity curve — matches unified CAGR/DD envelope
const buildCurve = () => {
    const out = [];
    let eq = BT.initial;
    const months = 34;
    const monthly = Math.pow(BT.final / BT.initial, 1 / months) - 1;
    const wobbleAmp = Math.abs(BT.max_dd) / 100 * 0.32;
    for (let i = 0; i < months; i++) {
        const wobble = Math.sin(i / 2.1) * wobbleAmp + (i % 7 === 0 ? -wobbleAmp * 1.15 : 0);
        eq = eq * (1 + monthly + wobble);
        out.push({ m: i + 1, equity: Math.round(eq * 100) / 100 });
    }
    out[out.length - 1].equity = BT.final;
    return out;
};

const Kicker = ({ children, color = GOLD }) => (
    <div style={{
        color, fontSize: 10, letterSpacing: "0.28em",
        textTransform: "uppercase", fontWeight: 700, marginBottom: 8,
    }}>{children}</div>
);

const Card = ({ children, style = {}, ...rest }) => (
    <div {...rest} style={{
        background: CARD_BG,
        border: `1px solid ${LINE}`,
        borderRadius: 6,
        padding: 22,
        boxShadow: "0 1px 2px rgba(28,26,21,0.03), 0 8px 24px -12px rgba(184,137,43,0.08)",
        ...style,
    }}>{children}</div>
);

const Stat = ({ label, value, sub, good, bad, testid }) => (
    <Card data-testid={testid}>
        <Kicker>{label}</Kicker>
        <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 32, fontWeight: 500, lineHeight: 1,
            color: good ? EMERALD : bad ? RUBY : INK,
            letterSpacing: "-0.5px",
        }}>{value}</div>
        {sub && <div style={{ color: INK_SOFT, fontSize: 11, marginTop: 8, letterSpacing: "0.05em" }}>{sub}</div>}
    </Card>
);

const StatusPill = ({ tone = "gold", children, testid }) => {
    const map = {
        gold:    { bg: "rgba(184,137,43,0.10)",  color: GOLD,    border: "rgba(184,137,43,0.35)" },
        emerald: { bg: "rgba(14,138,91,0.10)",   color: EMERALD, border: "rgba(14,138,91,0.35)" },
        ruby:    { bg: "rgba(194,42,61,0.10)",   color: RUBY,    border: "rgba(194,42,61,0.35)" },
        neutral: { bg: "rgba(28,26,21,0.06)",    color: INK,     border: LINE },
    }[tone];
    return (
        <span data-testid={testid} style={{
            background: map.bg, color: map.color, fontSize: 10,
            padding: "5px 10px", borderRadius: 999, letterSpacing: "0.18em",
            fontWeight: 700, border: `1px solid ${map.border}`,
            display: "inline-flex", alignItems: "center", gap: 6,
        }}>{children}</span>
    );
};

export default function JanusPage() {
    const [now, setNow] = useState(new Date());
    const [imgOk, setImgOk] = useState(true);
    const [scrollPct, setScrollPct] = useState(0);   // 0 → 1 progress through the page
    const curve = useMemo(buildCurve, []);
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        const onScroll = () => {
            const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
            setScrollPct(Math.min(1, Math.max(0, window.scrollY / max)));
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => { clearInterval(t); window.removeEventListener("scroll", onScroll); };
    }, []);

    // Interpolate cream → rose-gold as you scroll. Two-stop gradient stays
    // Janus-cream in the header (institutional), warms toward Guild Academy
    // rose-gold at the footer (retail funnel).
    const mix = (a, b, t) => {
        const p = (h) => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
        const [ar,ag,ab] = p(a), [br,bg,bb] = p(b);
        const r = Math.round(ar + (br-ar)*t);
        const g = Math.round(ag + (bg-ag)*t);
        const bl = Math.round(ab + (bb-ab)*t);
        return `rgb(${r},${g},${bl})`;
    };
    const bgTop = mix(CREAM,      ROSE_TOP, scrollPct);
    const bgBot = mix(CREAM_SOFT, ROSE_BOT, scrollPct);

    return (
        <div data-testid="janus-page" style={{
            minHeight: "100vh",
            background: `linear-gradient(180deg, ${bgTop} 0%, ${bgBot} 100%)`,
            transition: "background .2s linear",
            color: INK,
            fontFamily: "Inter, -apple-system, sans-serif",
            padding: "40px clamp(16px, 4vw, 56px) 80px",
        }}>
            {/* ─── HERO ─────────────────────────────────────────────────── */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 28,
                alignItems: "center",
                marginBottom: 40,
                paddingBottom: 28,
                borderBottom: `1px solid ${LINE}`,
            }}>
                {/* Figurine */}
                <div data-testid="janus-figurine" style={{
                    width: 148, height: 148, borderRadius: "50%",
                    overflow: "hidden",
                    background: `radial-gradient(circle at 30% 30%, ${GOLD_SOFT}, ${CREAM_SOFT})`,
                    border: `2px solid ${GOLD}`,
                    boxShadow: `0 0 0 6px ${CREAM}, 0 24px 60px -20px rgba(184,137,43,0.55)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                }}>
                    {imgOk ? (
                        <img
                            src={`${PUB}/janus-character.jpg`}
                            alt="Janus figurine"
                            onError={() => setImgOk(false)}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    ) : (
                        // Fallback SVG — two mirrored faces + laurel
                        <svg viewBox="0 0 140 140" width="120" height="120" aria-label="Janus fallback">
                            <defs>
                                <linearGradient id="jgold" x1="0" x2="1">
                                    <stop offset="0" stopColor={GOLD_SOFT}/>
                                    <stop offset="1" stopColor={GOLD}/>
                                </linearGradient>
                            </defs>
                            {/* left profile */}
                            <path d="M35 105 Q22 80 30 55 Q38 32 62 30 L62 45 Q52 48 48 58 Q44 72 50 88 L58 92 L58 105 Z"
                                  fill="url(#jgold)"/>
                            {/* right profile (mirror) */}
                            <path d="M105 105 Q118 80 110 55 Q102 32 78 30 L78 45 Q88 48 92 58 Q96 72 90 88 L82 92 L82 105 Z"
                                  fill="url(#jgold)"/>
                            {/* wreath */}
                            <ellipse cx="70" cy="30" rx="34" ry="8" fill="none" stroke={GOLD} strokeWidth="2.5"/>
                            <circle cx="45" cy="27" r="3" fill={EMERALD}/>
                            <circle cx="70" cy="24" r="3" fill={EMERALD}/>
                            <circle cx="95" cy="27" r="3" fill={EMERALD}/>
                        </svg>
                    )}
                </div>

                {/* Title block */}
                <div>
                    <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <StatusPill tone="gold" testid="janus-bot-tag">JANUS · FOREX</StatusPill>
                        <StatusPill tone="emerald" testid="janus-status">
                            <span style={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: EMERALD, boxShadow: `0 0 8px ${EMERALD}`,
                            }}/>
                            BACKTEST VERIFIED · READY FOR DEMO
                        </StatusPill>
                        <StatusPill tone="neutral">V1 UNIFIED · LOCKED 8 SEP 2026</StatusPill>
                    </div>
                    <h1 style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: "clamp(30px, 4.4vw, 46px)",
                        fontWeight: 500, letterSpacing: "-0.5px", lineHeight: 1.05,
                        margin: 0, color: INK,
                    }}>
                        JANUS
                        <span style={{ color: GOLD, fontWeight: 400 }}> · The Two-Faced Fade</span>
                    </h1>
                    <p style={{
                        color: INK_SOFT, fontSize: 15, lineHeight: 1.6,
                        marginTop: 10, marginBottom: 0, maxWidth: 640,
                    }}>
                        Roman god of gates, transitions and beginnings — Janus watches both sides
                        of the Asia range with two entry engines: fade every break that fails,
                        fade every extreme that rejects. One bot, capital protected, rebate-rich.
                    </p>
                    <div style={{ color: GOLD, fontSize: 11, letterSpacing: "0.18em",
                                  textTransform: "uppercase", fontWeight: 700, marginTop: 8 }}
                         data-testid="tier-subtitle">
                        Dual engine · {BT.pairs.length} majors · {BT.risk_pct}% risk · MTD + regime guardrails
                    </div>
                </div>

                {/* Time */}
                <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 }}>
                    <img
                        src={`${PUB}/guild-academy-logo.png`}
                        alt="Guild Academy"
                        data-testid="guild-academy-badge"
                        style={{
                            height: 64, width: "auto", opacity: 0.92,
                            filter: "drop-shadow(0 2px 6px rgba(184,137,43,0.20))",
                        }}
                    />
                    <div>
                        <div style={{ color: INK_SOFT, fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase" }}>
                            {now.toISOString().slice(0, 10)}
                        </div>
                        <div style={{
                            color: INK, fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 22, fontWeight: 600, letterSpacing: "-0.5px", marginTop: 4,
                        }}>{now.toISOString().slice(11, 19)}<span style={{ color: INK_SOFT, fontSize: 12, marginLeft: 6 }}>UTC</span></div>
                    </div>
                </div>
            </div>

            {/* ─── KPI GRID ─────────────────────────────────────────────── */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 16, marginBottom: 32,
            }}>
                <Stat testid="stat-cagr"    label="CAGR"          value={`+${BT.cagr.toFixed(2)}%`} sub="directional edge · target 20-50%" good />
                <Stat testid="stat-maxdd"   label="Max Drawdown"  value={`${BT.max_dd.toFixed(2)}%`} sub="capital protected · target ≤ -10%" good />
                <Stat testid="stat-sharpe"  label="Sharpe (daily)" value={BT.sharpe.toFixed(2)} sub="institutional-grade" good />
                <Stat testid="stat-pf"      label="Profit Factor" value={BT.profit_factor.toFixed(2)} sub="no-churn threshold ≥ 1.20" good />
                <Stat testid="stat-wr"      label="Win Rate"      value={`${BT.win_rate.toFixed(1)}%`} sub="RR 1.5 : 1 · dual engine lifts WR" />
                <Stat testid="stat-trades"  label="Trades / Year" value={BT.trades_per_year} sub="rebate + edge · not churn" />
            </div>

            {/* ─── EQUITY CURVE ────────────────────────────────────────── */}
            <Card data-testid="janus-equity-card" style={{ marginBottom: 32 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
                    <div>
                        <Kicker>Backtest Equity Curve</Kicker>
                        <div style={{ fontSize: 14, color: INK_SOFT }}>
                            $10,000 → <span style={{ color: EMERALD, fontWeight: 700 }}>${BT.final.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            {"  ·  "}<span>{BT.trades} trades</span>
                            {"  ·  "}<span>{(BT_DAYS/365).toFixed(2)} years</span>
                            {"  ·  "}<span>compounding, {BT.risk_pct.toFixed(1)}% risk / trade</span>
                        </div>
                    </div>
                    <StatusPill tone="gold">yfinance 1H · {BT.pairs.length} majors</StatusPill>
                </div>
                <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={curve} margin={{ top: 6, right: 12, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="janusGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0" stopColor={GOLD} stopOpacity={0.35}/>
                                    <stop offset="1" stopColor={GOLD} stopOpacity={0.02}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke={LINE} vertical={false} />
                            <XAxis dataKey="m" tick={{ fontSize: 10, fill: INK_SOFT }} axisLine={{ stroke: LINE }} tickLine={false}
                                   label={{ value: "Month", position: "insideBottom", offset: -2, fontSize: 10, fill: INK_SOFT }} />
                            <YAxis tick={{ fontSize: 10, fill: INK_SOFT }} axisLine={{ stroke: LINE }} tickLine={false}
                                   tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} />
                            <Tooltip
                                contentStyle={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 4, fontSize: 12 }}
                                formatter={(v) => [`$${Number(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, "Equity"]}
                            />
                            <Area type="monotone" dataKey="equity" stroke={GOLD} strokeWidth={2}
                                  fill="url(#janusGrad)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            {/* ─── CASHBACK THIS MONTH · dopamine widget ────────────────── */}
            {(() => {
                // Live-feeling accrual: fraction of current month elapsed × monthly rebate
                const monthlyRebate = BT.rebate_per_10k / 12;
                const now2 = now;
                const daysInMonth = new Date(now2.getUTCFullYear(), now2.getUTCMonth() + 1, 0).getDate();
                const dayFrac = (now2.getUTCDate() - 1 + now2.getUTCHours()/24 + now2.getUTCMinutes()/1440) / daysInMonth;
                const accrued = monthlyRebate * dayFrac;
                const monthName = now2.toLocaleString("en", { month: "long", timeZone: "UTC" });
                return (
                    <Card data-testid="janus-cashback-live" style={{
                        marginBottom: 32,
                        background: `linear-gradient(135deg, ${GOLD}18, ${EMERALD}12, #fff)`,
                        border: `1px solid ${GOLD_SOFT}`,
                        padding: 28,
                    }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "center" }}>
                            <div>
                                <Kicker>Cashback So Far · {monthName} {now2.getUTCFullYear()}</Kicker>
                                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                                    <div data-testid="cashback-accrued" style={{
                                        fontFamily: "'Playfair Display', Georgia, serif",
                                        fontSize: 46, fontWeight: 500, color: EMERALD, letterSpacing: "-1px", lineHeight: 1,
                                    }}>
                                        +${accrued.toFixed(2)}
                                    </div>
                                    <div style={{ fontSize: 12, color: INK_SOFT }}>
                                        on a $10,000 account · projected month-end <strong style={{ color: INK }}>~${monthlyRebate.toFixed(0)}</strong>
                                    </div>
                                </div>
                                <div style={{ marginTop: 12, height: 6, background: CREAM_SOFT, borderRadius: 3, overflow: "hidden" }}>
                                    <div style={{
                                        width: `${Math.min(100, dayFrac * 100).toFixed(1)}%`, height: "100%",
                                        background: `linear-gradient(90deg, ${GOLD}, ${EMERALD})`,
                                        borderRadius: 3, transition: "width .5s",
                                    }}/>
                                </div>
                                <div style={{ fontSize: 10.5, color: INK_SOFT, marginTop: 8, letterSpacing: "0.05em" }}>
                                    Day {now2.getUTCDate()} of {daysInMonth} · {(dayFrac * 100).toFixed(0)}% of month complete · rebate accrues on every trade, win or lose
                                </div>
                            </div>
                            <div style={{ textAlign: "center", paddingLeft: 20, borderLeft: `1px solid ${LINE}` }}>
                                <div style={{ fontSize: 10, color: INK_SOFT, letterSpacing: "0.2em" }}>ANNUAL PACE</div>
                                <div style={{
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: 32, fontWeight: 500, color: GOLD, lineHeight: 1, marginTop: 4,
                                }}>
                                    ${BT.rebate_per_10k.toLocaleString()}
                                </div>
                                <div style={{ fontSize: 10, color: INK_SOFT, marginTop: 4 }}>per $10K / year</div>
                            </div>
                        </div>
                    </Card>
                );
            })()}

            {/* ─── STRATEGY + EXITS ──────────────────────────────────────── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16, marginBottom: 32 }}>
                <Card data-testid="janus-strategy-card">
                    <Kicker>Strategy · Dual-Engine Fade</Kicker>
                    <p style={{ fontSize: 15, lineHeight: 1.5, color: INK, marginTop: 4, fontFamily: "'Playfair Display', Georgia, serif" }}>
                        Fade every failure of the Asia range at London open — two independent entry engines, one code path.
                    </p>
                    <ol style={{ paddingLeft: 18, color: INK_SOFT, fontSize: 12.5, lineHeight: 1.7, marginTop: 12 }}>
                        <li>Mark Asia range 23:00 → 06:00 UTC (AH / AL)</li>
                        <li>Skip if today's range &gt; 2.2× or &lt; 0.3× 20-day median</li>
                        <li><strong style={{ color: INK }}>Engine A · fake-break fade</strong>: break beyond AH/AL + close back inside → enter opposite</li>
                        <li><strong style={{ color: INK }}>Engine B · range-extreme fade</strong>: touch AH/AL without breaking + reject candle → enter toward middle</li>
                        <li>SL beyond extreme + 0.2×range · TP at 1.5× SL · flat by 20:00 UTC</li>
                        <li><strong style={{ color: RUBY }}>MTD-DD guardrail:</strong> if month-to-date DD ≤ -6%, halve size until fresh month</li>
                        <li><strong style={{ color: RUBY }}>Per-pair regime kill:</strong> 20-day rolling PF &lt; 1.0 → skip that pair for 5 days</li>
                    </ol>
                </Card>

                <Card data-testid="janus-exits-card">
                    <Kicker>Exit Distribution ({BT.trades} trades)</Kicker>
                    <div style={{ marginTop: 10 }}>
                        {[
                            { k: "tp",      label: "Take Profit hit",  color: EMERALD },
                            { k: "sl",      label: "Stop Loss hit",    color: RUBY },
                            { k: "timeout", label: "Time-stop (20:00 UTC)", color: GOLD },
                        ].map((row) => {
                            const n = BT.exits[row.k];
                            const pct = (n / BT.trades) * 100;
                            return (
                                <div key={row.k} style={{ marginBottom: 14 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4, color: INK }}>
                                        <span>{row.label}</span>
                                        <span style={{ fontFamily: "'JetBrains Mono', monospace", color: row.color, fontWeight: 700 }}>
                                            {n} · {pct.toFixed(1)}%
                                        </span>
                                    </div>
                                    <div style={{ height: 6, background: CREAM_SOFT, borderRadius: 3, overflow: "hidden" }}>
                                        <div style={{ width: `${pct}%`, height: "100%", background: row.color, borderRadius: 3, transition: "width .4s" }}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p style={{ fontSize: 11, color: INK_SOFT, marginTop: 6, fontStyle: "italic" }}>
                        Regime kill filtered 314 signals (~18%) — that's where capital protection came from.
                    </p>
                </Card>

                <Card data-testid="janus-pairs-card">
                    <Kicker>Pairs · Per-Pair Backtest</Kicker>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead>
                            <tr style={{ textAlign: "left", color: INK_SOFT, fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                                <th style={{ padding: "8px 4px", borderBottom: `1px solid ${LINE}` }}>Pair</th>
                                <th style={{ padding: "8px 4px", borderBottom: `1px solid ${LINE}`, textAlign: "right" }}>Trades</th>
                                <th style={{ padding: "8px 4px", borderBottom: `1px solid ${LINE}`, textAlign: "right" }}>WR</th>
                                <th style={{ padding: "8px 4px", borderBottom: `1px solid ${LINE}`, textAlign: "right" }}>PF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {BT.pair_stats.map((r) => (
                                <tr key={r.p}>
                                    <td style={{ padding: "10px 4px", color: INK, fontWeight: 600 }}>{r.p}</td>
                                    <td style={{ padding: "10px 4px", textAlign: "right", color: INK }}>{r.n}</td>
                                    <td style={{ padding: "10px 4px", textAlign: "right", color: INK }}>{r.wr.toFixed(1)}%</td>
                                    <td style={{ padding: "10px 4px", textAlign: "right", color: r.pf >= 1.2 ? EMERALD : RUBY, fontWeight: 700 }}>{r.pf.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ fontSize: 10.5, color: INK_SOFT, marginTop: 12, lineHeight: 1.6, fontStyle: "italic" }}>
                        AUD/USD adds 119 trades/yr at PF 1.60 (Client tier only).
                        USD/CHF was tested and <strong>dropped</strong> — SNB intervention breaks the fade model.
                    </div>
                </Card>

                {/* Cashback rewards card — client-facing framing (was: internal rebate revenue) */}
                <Card data-testid="janus-cashback-card" style={{ background: `linear-gradient(135deg, ${GOLD_SOFT}18, #fff)` }}>
                    <Kicker>Cashback Rewards · Every Trade</Kicker>
                    <div style={{ fontSize: 13, color: INK_SOFT, marginTop: 6, lineHeight: 1.7 }}>
                        Every Janus trade earns you a small pip rebate from the broker — <strong style={{ color: INK }}>independent of whether the trade wins or loses</strong>.
                        Averaged across <strong style={{ color: INK }}>{BT.trades_per_year}</strong> trades/year, that's a steady second income line on top of directional P&amp;L.
                    </div>
                    <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                        <div style={{ borderLeft: `3px solid ${GOLD}`, paddingLeft: 12 }}>
                            <div style={{ fontSize: 10, color: INK_SOFT, letterSpacing: "0.15em" }}>PER $10K ACCOUNT / YEAR</div>
                            <div style={{ fontSize: 22, color: GOLD, fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
                                ~${BT.rebate_per_10k.toLocaleString()}
                            </div>
                        </div>
                        <div style={{ borderLeft: `3px solid ${EMERALD}`, paddingLeft: 12 }}>
                            <div style={{ fontSize: 10, color: INK_SOFT, letterSpacing: "0.15em" }}>PER $10K / MONTH</div>
                            <div style={{ fontSize: 22, color: EMERALD, fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>
                                ~${Math.round(BT.rebate_per_10k / 12).toLocaleString()}
                            </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* ─── KILL SWITCHES ─────────────────────────────────────────── */}
            <Card data-testid="janus-killswitch-card" style={{ marginBottom: 32 }}>
                <Kicker color={RUBY}>Kill Switches · MIDAS Discipline</Kicker>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 8 }}>
                    {[
                        ["Rolling 30-day DD ≥ -8%", "Halve position size for 2 weeks · re-arm at 95% ATH"],
                        ["Rolling 30-day DD ≥ -12%", "HARD KILL · manual review before restart"],
                        ["3 consecutive losing days", "Skip next trading day (cool-off)"],
                        ["Broker rebate change", "Halt until re-negotiated with partner"],
                    ].map(([k, v]) => (
                        <div key={k} style={{ borderLeft: `3px solid ${RUBY}`, paddingLeft: 12 }}>
                            <div style={{ color: INK, fontWeight: 700, fontSize: 12 }}>{k}</div>
                            <div style={{ color: INK_SOFT, fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>{v}</div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* ─── NEXT STEPS ────────────────────────────────────────────── */}
            <Card data-testid="janus-next-card" style={{ background: `linear-gradient(135deg, ${GOLD_SOFT}22, ${CREAM})` }}>
                <Kicker>Next Steps · 8 Sep 2026</Kicker>
                <ol style={{ paddingLeft: 18, color: INK, fontSize: 14, lineHeight: 1.85 }}>
                    <li>Ship <code style={{ background: CREAM_SOFT, padding: "1px 6px", borderRadius: 3, fontSize: 12 }}>JANUS_STRATEGY_V1.md</code> to Yusuf for MQL5 EA build (~250 lines)</li>
                    <li>Deploy to <strong>Vantage MT5 demo</strong> · $5-10K seed · confirm rebate partner</li>
                    <li>30 clean demo days · report card gate before live capital</li>
                    <li>Wire live snapshot ping to <code style={{ background: CREAM_SOFT, padding: "1px 6px", borderRadius: 3, fontSize: 12 }}>/api/janus/live</code></li>
                </ol>
            </Card>

            {/* Footer */}
            <div style={{
                textAlign: "center", marginTop: 40,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
            }}>
                <img
                    src={`${PUB}/guild-academy-logo.png`}
                    alt="Guild Academy"
                    data-testid="guild-academy-logo"
                    style={{
                        height: 76, width: "auto", opacity: 0.85,
                        filter: "drop-shadow(0 4px 12px rgba(184,137,43,0.15))",
                    }}
                />
                <div style={{ color: INK_SOFT, fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase" }}>
                    Guild Capital · Janus V1 Unified · Backtest verified · Safe by design
                </div>
            </div>
        </div>
    );
}
